import type {
  AuthenticateResponseData,
  AssessmentRoomsResponseData,
  AssessmentDetailsResponseData,
  CategoriesResponseData,
  QuestionApi,
  QuestionPatchApi,
  SaveAnswerResponseData,
  ChatMessageApi,
} from './types.api';
import type {
  AuthSessionData,
  AssessmentDetails,
  Recommendation,
  RoomSummary,
  RoomDetail,
  Section,
  Question,
  QuestionStatus,
  RoomStatus,
  SaveAnswerRequest,
  ChatMessage,
} from './types';

// Keyword rules checked in priority order — first match wins.
// Uses word-boundary regex to avoid false partial matches (e.g. "ai" inside "training").
const KEYWORD_ICON_RULES: [RegExp, string][] = [
  [/\bai\b/i, 'bot'],
  [/\bprivileged\b/i, 'shield-alert'],
  [/\baccess\s+review\b/i, 'user-check'],
  [/\bidentity\s+lifecycle\b/i, 'user-check'],
  [/\bsubprocessor\b/i, 'link'],
  [/\bsupply\s+chain\b/i, 'link'],
  [/\bdisaster\s+recovery\b/i, 'life-buoy'],
  [/\bbusiness\s+continuity\b/i, 'refresh-ccw'],
  [/\bbackup\b/i, 'database-backup'],
  [/\bincident\b/i, 'siren'],
  [/\bthreat\b/i, 'eye'],
  [/\b(logging|log\s+management)\b/i, 'bar-chart-3'],
  [/\bmonitoring\b/i, 'bar-chart-3'],
  [/\b(sdlc|secure\s+software|software\s+development\s+lifecycle)\b/i, 'git-branch'],
  [/\bapplication\b/i, 'app-window'],
  [/\bvulnerabilit/i, 'bandage'],
  [/\bpatch\b/i, 'bandage'],
  [/\bendpoint\b/i, 'laptop'],
  [/\bnetwork\b/i, 'network'],
  [/\b(cloud|infrastructure)\b/i, 'cloud-alert'],
  [/\b(cryptography|encryption|key\s+management)\b/i, 'key'],
  [/\b(retention|disposal)\b/i, 'trash-2'],
  [/\bclassification\b/i, 'tag'],
  [/\b(privacy|personal\s+data)\b/i, 'lock'],
  [/\bdata\b/i, 'lock'],
  [/\b(physical|environmental)\b/i, 'building'],
  [/\bthird[- ]party\b/i, 'handshake'],
  [/\bvendor\b/i, 'handshake'],
  [/\b(workforce|personnel)\b/i, 'users'],
  [/\b(human\s+resources?|hr)\b/i, 'users'],
  [/\b(training|awareness)\b/i, 'graduation-cap'],
  [/\b(authentication|credential)\b/i, 'key-round'],
  [/\bidentity\b/i, 'user'],
  [/\b(configuration|hardening)\b/i, 'settings'],
  [/\b(asset|inventory)\b/i, 'boxes'],
  [/\b(compliance|regulatory)\b/i, 'clipboard-check'],
  [/\brisk\b/i, 'alert-triangle'],
  [/\b(policy|policies|standards?)\b/i, 'file-text'],
  [/\bgovernance\b/i, 'shield-check'],
  [/\baccess\b/i, 'shield'],
  [/\bsecurity\b/i, 'shield-check'],
];

export function roomNameToIcon(name: string): string {
  // Normalize underscores to spaces so snake_case names match properly
  const normalized = name.replace(/_/g, ' ');
  for (const [pattern, icon] of KEYWORD_ICON_RULES) {
    if (pattern.test(normalized)) return icon;
  }
  return 'shield';
}

// --- Derive room status from counts ---
export function deriveRoomStatus(
  _requiredAnswered: number,
  _totalRequired: number,
  answered: number,
  total: number,
): RoomStatus {
  if (total === 0 && answered === 0) return 'not-started';
  if (answered === 0) return 'not-started';
  if (answered >= total) return 'complete';
  return 'in-progress';
}

// --- Auth transform ---
export function transformAuthResponse(data: AuthenticateResponseData): AuthSessionData {
  return {
    vendorName: data.vendor.name,
    vendorLogoUrl: data.vendor.logo_url,
    clientName: data.client.name,
    clientLogoUrl: data.client.logo_url,
    contactName: `${data.vendor.user.first_name} ${data.vendor.user.last_name}`.trim(),
    contactEmail: data.vendor.user.email,
    assessmentStatus: data.assessment.status.replace(
      /_/g,
      '-',
    ) as AuthSessionData['assessmentStatus'],
    submittedAt: data.assessment.submitted_at ?? null,
    chatId: data.assessment.chat_id ?? null,
    questionsAnswered: data.assessment.metrics.questions.completed,
    questionsTotal: data.assessment.metrics.questions.total,
    questionsRemaining: data.assessment.metrics.questions.remaining,
    documentsCompleted: data.assessment.metrics.documents.completed,
    documentsTotal: data.assessment.metrics.documents.total,
    documentsRemaining: data.assessment.metrics.documents.remaining,
    primaryColor: data.client.primary_color ?? null,
    backgroundColor: data.client.background_color ?? null,
  };
}

// --- Rooms transform ---
export function transformRooms(data: AssessmentRoomsResponseData): {
  rooms: RoomSummary[];
  recommendation: Recommendation | null;
  allRequiredQuestionsAnswered: boolean;
  allRequiredEvidencesUploaded: boolean;
} {
  const rooms = data.assessment_rooms.map((room, index) => ({
    id: room.id,
    label: room.name,
    iconName: roomNameToIcon(room.name),
    status: deriveRoomStatus(
      room.questions.required_answered + room.documents.required_uploaded,
      room.questions.total_required + room.documents.total_required,
      room.questions.answered + room.documents.uploaded,
      room.questions.total + room.documents.total,
    ),
    order: index + 1,
    totalQuestions: room.questions.total,
    answeredQuestions: room.questions.answered,
    requiredAnswered: room.questions.required_answered,
    totalRequired: room.questions.total_required,
    totalDocuments: room.documents.total,
    uploadedDocuments: room.documents.uploaded,
    requiredDocuments: room.documents.total_required,
    requiredUploaded: room.documents.required_uploaded,
  }));

  const rec = data.recommendation;
  const recommendation: Recommendation | null = rec
    ? { header: rec.header, message: rec.message, assessmentRoomId: rec.assessment_room_id }
    : null;

  return {
    rooms,
    recommendation,
    allRequiredQuestionsAnswered: data.all_required_questions_answered ?? false,
    allRequiredEvidencesUploaded: data.all_required_evidences_uploaded ?? false,
  };
}

// --- Assessment details transform ---
export function transformDetails(data: AssessmentDetailsResponseData): AssessmentDetails {
  return {
    id: data.assessment.id,
    totalQuestions: data.assessment.total_questions,
    totalQuestionsAnswered: data.assessment.total_questions_answered,
    percentageCompletion: data.assessment.percentage_completion,
    dueDate: data.assessment.due_date,
    daysLeft: data.assessment.days_left,
    clientOrgName: data.client.org.name,
    clientContactName: `${data.client.user.first_name} ${data.client.user.last_name}`,
    clientContactEmail: data.client.user.email,
    tierLabel: data.vendor.tier.label,
    tierValue: data.vendor.tier.value,
  };
}

// --- Question transform ---
function qStatus(hasAnswer: boolean): QuestionStatus {
  return hasAnswer ? 'answered' : 'unanswered';
}

export function transformQuestion(q: QuestionApi, sectionId: string): Question {
  const base = {
    id: q.id,
    text: q.text,
    required: q.required,
    questionNumber: q.question_number,
    sectionId,
  };

  // Pre-filled override
  if (q.pre_filled_response !== null) {
    const response: 'confirmed' | 'updated' | null =
      q.answer === 'confirmed' ? 'confirmed' : q.answer === 'updated' ? 'updated' : null;

    return {
      ...base,
      type: 'prefilled-confirm' as const,
      status: qStatus(q.answer !== null),
      prefilledAnswer: q.pre_filled_response,
      sourceAttribution: null,
      response,
      updatedAnswer:
        typeof q.answer === 'string' && q.answer !== 'confirmed' && q.answer !== 'updated'
          ? q.answer
          : null,
    };
  }

  switch (q.question_type) {
    case 'free_text':
      return {
        ...base,
        type: 'textarea' as const,
        status: qStatus(typeof q.answer === 'string' && q.answer !== ''),
        answer: typeof q.answer === 'string' ? q.answer : '',
      };

    case 'single_choice': {
      const choices = q.choices;
      const isYesNo =
        !q.dropdown_single_choice &&
        choices.length === 2 &&
        choices.some((c) => c.toLowerCase() === 'yes') &&
        choices.some((c) => c.toLowerCase() === 'no');

      if (isYesNo) {
        return {
          ...base,
          type: 'yes-no' as const,
          status: qStatus(q.answer !== null),
          answer:
            q.answer === null
              ? null
              : typeof q.answer === 'string' && q.answer.toLowerCase() === 'yes',
        };
      }

      return {
        ...base,
        type: 'dropdown' as const,
        status: qStatus(q.answer !== null),
        options: choices.map((c: string) => ({ value: c, label: c })),
        selectedValue: typeof q.answer === 'string' ? q.answer : null,
      };
    }

    case 'multiple_choice': {
      const answerList = Array.isArray(q.answer) ? q.answer : [];
      return {
        ...base,
        type: 'multi-select' as const,
        status: qStatus(answerList.length > 0),
        options: q.choices.map((c) => ({
          id: c,
          label: c,
          checked: answerList.includes(c),
        })),
      };
    }

    case 'file_attachment':
      return {
        ...base,
        type: 'file-attachment' as const,
        status: qStatus(q.answer !== null),
        answer: typeof q.answer === 'string' ? q.answer : null,
      };
  }
}

// --- Transform a QuestionPatchApi (PATCH response) ---
function transformPatchQuestion(q: QuestionPatchApi, sectionId: string): Question {
  return transformQuestion(q, sectionId);
}

// --- Section status helper ---
function deriveSectionStatus(questions: Question[]): 'pending' | 'complete' {
  return questions.length > 0 && questions.every((q) => q.status === 'answered')
    ? 'complete'
    : 'pending';
}

// --- Categories → RoomDetail ---
export function transformCategories(data: CategoriesResponseData): RoomDetail {
  const sections: Section[] = data.assessment_room.categories.map((cat, index) => {
    const questions = cat.questions.map((q) => transformQuestion(q, cat.id));
    const answeredCount = questions.filter((q) => q.status === 'answered').length;

    return {
      id: cat.id,
      title: cat.name,
      order: index + 1,
      status: deriveSectionStatus(questions),
      totalQuestions: questions.length,
      answeredQuestions: answeredCount,
      questions,
    };
  });

  const totalQuestions = sections.reduce((sum, s) => sum + s.totalQuestions, 0);
  const answeredQuestions = sections.reduce((sum, s) => sum + s.answeredQuestions, 0);

  const totalEvidences = data.assessment_room.evidences?.length ?? 0;
  const uploadedEvidences = (data.assessment_room.evidences ?? []).filter(
    (ev) => ev.number_of_files > 0,
  ).length;

  return {
    id: data.assessment_room.id,
    label: data.assessment_room.name,
    iconName: roomNameToIcon(data.assessment_room.name),
    status: deriveRoomStatus(
      answeredQuestions + uploadedEvidences,
      totalQuestions + totalEvidences,
      answeredQuestions + uploadedEvidences,
      totalQuestions + totalEvidences,
    ),
    order: 0,
    totalQuestions,
    answeredQuestions,
    sections,
    documents: (data.assessment_room.all_documents ?? []).map((doc, i) => ({
      id: `doc-${String(i)}`,
      name: doc.file_name,
      fileType: doc.file_name.split('.').pop() ?? 'unknown',
      fileUrl: doc.file_url,
      uploadedAt: null,
    })),
    evidences: (data.assessment_room.evidences ?? []).map((ev) => ({
      id: ev.id,
      documentName: ev.document_name,
      sourceUrls: ev.source_urls,
      fileNames: ev.file_names,
      numberOfFiles: ev.number_of_files,
      required: ev.required,
      maxNumberOfFiles: ev.max_number_of_files,
    })),
  };
}

// --- Save answer: FE → BE ---
export function toSaveAnswerRequest(body: SaveAnswerRequest): { answer: string | string[] | null } {
  switch (body.type) {
    case 'prefilled-confirm':
      return {
        answer:
          body.response === 'updated' && body.updatedAnswer ? body.updatedAnswer : body.response,
      };
    case 'multi-select':
      return { answer: body.options.filter((o) => o.checked).map((o) => o.id) };
    case 'dropdown':
      return { answer: body.selectedValue };
    case 'textarea':
      return { answer: body.answer };
    case 'yes-no':
      return { answer: body.answer ? 'Yes' : 'No' };
    case 'file-attachment':
      return { answer: body.answer };
  }
}

// --- Save answer: BE → FE ---
export function transformSaveResponse(
  data: SaveAnswerResponseData,
  sectionId: string,
): { question: Question; metadata: { totalQuestions: number; answeredQuestions: number } } {
  return {
    question: transformPatchQuestion(data.question, sectionId),
    metadata: {
      totalQuestions: data.assessment_room_metadata.total_questions,
      answeredQuestions: data.assessment_room_metadata.total_questions_answered,
    },
  };
}

// --- Chat message transform ---
export function transformChatMessage(msg: ChatMessageApi, fallbackChatId?: string): ChatMessage {
  // Normalize 'agent' role to 'assistant' (BE may send Python enum)
  const role: 'user' | 'assistant' = msg.role === 'user' ? 'user' : 'assistant';

  return {
    id: msg.id,
    chatId: msg.chat_id ?? fallbackChatId ?? '',
    role,
    type: msg.type,
    text: msg.text,
    createdAt: msg.created_at,
    attachments: msg.attachments.map((a, i) => ({
      // BE history responses omit the attachment id — synthesize a stable one for the key.
      id: a.id ?? `att-${msg.id}-${String(i)}`,
      fileName: a.file_name,
      signedUrl: a.signed_url,
    })),
    reasoning: msg.reasoning?.map((r) => ({
      id: r.id,
      title: r.reason_for_vendor.title,
      description: r.reason_for_vendor.description,
      followUpQuestionId: r.follow_up_question_id,
      followUpQuestionIndex: r.follow_up_question_index,
    })),
  };
}
